/**
 * Trends API - Clinical data trends and analytics
 */
import { apiClient } from '../api-client';

// ==================== Types ====================

export interface FEV1TrendData {
  date: string;
  actual: number | null;
  predicted: number;
  lowerLimit: number;
  upperLimit: number;
  zScore: number;
}

export interface FEV1TrendResponse {
  patient_id: number;
  metric: string;
  unit: string;
  period: string;
  data: FEV1TrendData[];
  summary: {
    total_tests: number;
    latest_value: number | null;
    trend_direction: string;
  };
}

export interface SixMWTTrendData {
  date: string;
  distance: number | null;
  baseline: number;
  target: number;
}

export interface SixMWTTrendResponse {
  patient_id: number;
  metric: string;
  unit: string;
  period: string;
  data: SixMWTTrendData[];
  summary: {
    total_tests: number;
    latest_distance: number | null;
    baseline: number;
    target: number;
    progress_percentage: number;
  };
}

export interface VitalsTrendData {
  date: string;
  spo2?: number | null;
  heart_rate?: number | null;
  systolic?: number | null;
  diastolic?: number | null;
  temperature?: number | null;
  weight?: number | null;
}

export interface VitalsTrendResponse {
  patient_id: number;
  metric: string;
  period: string;
  data: VitalsTrendData[];
  summary: {
    total_readings: number;
    days_with_data: number;
  };
}

export interface CombinedTrendData {
  week: string;
  fev1: number | null;
  sixMWD: number | null;
  spo2: number | null;
}

export interface CombinedTrendResponse {
  patient_id: number;
  period: string;
  data: CombinedTrendData[];
  summary: {
    fev1_tests: number;
    sixmwt_tests: number;
    vitals_readings: number;
  };
}

// ==================== API Functions ====================

/**
 * Get FEV1 trend data
 */
export async function getFEV1Trends(
  patientId: number,
  weeks: number = 6
): Promise<FEV1TrendResponse> {
  const response = await apiClient.get(`/trends/${patientId}/fev1-trends`, { weeks });
  return response;
}

/**
 * Get 6-Minute Walk Test trends
 */
export async function getSixMWTTrends(
  patientId: number,
  weeks: number = 6
): Promise<SixMWTTrendResponse> {
  const response = await apiClient.get(`/trends/${patientId}/sixmwt-trends`, { weeks });
  return response;
}

/**
 * Get vitals trends
 */
export async function getVitalsTrends(
  patientId: number,
  days: number = 30,
  metric: string = 'all'
): Promise<VitalsTrendResponse> {
  const response = await apiClient.get(`/trends/${patientId}/vitals-trends`, { days, metric });
  return response;
}

/**
 * Get combined clinical trends (FEV1, 6MWT, SpO2)
 * This is optimized for dashboard charts
 */
export async function getCombinedTrends(
  patientId: number,
  weeks: number = 6
): Promise<CombinedTrendResponse> {
  const response = await apiClient.get(`/trends/${patientId}/combined-trends`, { weeks });
  return response;
}

/**
 * Get current patient's own combined trends (for patient role)
 */
export async function getMyCombinedTrends(
  weeks: number = 6
): Promise<CombinedTrendResponse> {
  const response = await apiClient.get('/trends/me', { weeks });
  return response;
}